import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactDOM from 'react-dom';
import { runSaga } from 'redux-saga';
import { fetchBrandConfig, fetchBannerConfig } from 'capi/redux';
import { BRAND_CONFIG_FIELDS } from 'capi';

import amsIframeInjector from 'ams-iframe-injector';

import App from './app/App';
import appStore from './app/appStore';
import { getEnv, getIsOldUiPreferred, getStaticAssetUrl } from './app/bootstrap';
import { initializeIntl } from './intl/intlSagas';
import loadPolyfills from './app/polyfills';
import getWebfonts from './fontloaders/fontModule';
import { getOrCreateStorefrontSession } from './session/sessionModule';
import { requestFaceplateGroups } from './landingPage/landingPageModule';
import initializeIovation from './iovation/iovationModule';
import { createLocationChangeActionDispatcher } from './routing/routing';
import { setIsWebViewValue } from './webView/webViewModule';
import { getInitialPromo } from './promo/promoModule';
import { handlePreferredValues } from './routing/flowProgressModule';
import { initAddonWidget } from './utils/addonWidgetUtils';

const staticAssetUrl = getStaticAssetUrl();
if (staticAssetUrl) {
    
    __webpack_public_path__ = staticAssetUrl; 
}

const run = async () => {
    
    if (process.env.NODE_ENV === 'development') {
        
        const axe = require('@axe-core/react');
        axe(React, ReactDOM, 1000);

        
        const whyDidYouRender = require('@welldone-software/why-did-you-render');
        whyDidYouRender(React);
    }

    amsIframeInjector.client.addMessageListener(
        getEnv() || ''
    );

    // Check if loaded in in app webview or not and store the information as needed
    setIsWebViewValue(appStore.dispatch);

    // Create our CAPI session (WebRequestLog)
    const sessionAction = getOrCreateStorefrontSession();
    appStore.dispatch(sessionAction);

    // Dispatch handlePreferredValues FIRST so that the initializeFlow saga sets up
    // its `take` listeners BEFORE any brand config or faceplate actions can be dispatched.
    // This prevents a race condition where fast/cached API responses arrive before the
    // saga is ready to listen, causing it to deadlock waiting for actions that already passed.
    const handlePreferredValuesAction = handlePreferredValues();
    appStore.dispatch(handlePreferredValuesAction);

    // Start retrieving our initial Brand configuration
    const requiredFields = [
        BRAND_CONFIG_FIELDS.PREFERENCES,
        BRAND_CONFIG_FIELDS.CATALOG,
        BRAND_CONFIG_FIELDS.PAYMENT,
        BRAND_CONFIG_FIELDS.SHIPPING,
        BRAND_CONFIG_FIELDS.FEATURES,
        BRAND_CONFIG_FIELDS.BALANCE,
        BRAND_CONFIG_FIELDS.META,
        BRAND_CONFIG_FIELDS.PROGRAMS,
        BRAND_CONFIG_FIELDS.ACCESSORIES,
        BRAND_CONFIG_FIELDS.LIMITS
    ];
    const configAction = fetchBrandConfig(requiredFields);
    appStore.dispatch(configAction);

    // SBJ specific call : Fetch banner config only if old UI is preferred
    if (getIsOldUiPreferred()) {
        const bannerConfigData = fetchBannerConfig();
        appStore.dispatch(bannerConfigData);
    }

    const sagaArgs = { dispatch: appStore.dispatch, getState: appStore.getState };
    await Promise.all([
        // Load brand fonts - (fetch external stylesheets)
        getWebfonts(appStore),
        (runSaga(sagaArgs, initializeIntl)).done
        // Keep adding any other promises here, that
        // we want resolve during initialization
    ]);

    // Request Faceplates through our CAPI
    const requestFaceplatesAction = requestFaceplateGroups();
    appStore.dispatch(requestFaceplatesAction);


    // create location change action dispatcher
    createLocationChangeActionDispatcher(appStore);

    // Load the brand's preferred, stored or default promo if there is one
    const activeOrDefaultPromoAction = getInitialPromo();
    appStore.dispatch(activeOrDefaultPromoAction);

    
    await initAddonWidget();

    createRoot(document.getElementById('app-root')).render(<App />);

    
    
    
    initializeIovation(appStore);
};

loadPolyfills().then(() => {
    run();
});
